import React, { useState } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { ChevronDown } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@/components/ui/command";

interface GroupedItem {
  id: string;
  name: string;
  groupId: number;
  orderInGroup: number;
}

interface GroupedMultiSelectProps {
  items: GroupedItem[];
  selectedIds: string[];
  onToggleItem: (id: string) => void;
  placeholder: string;
  emptyMessage: string;
  searchPlaceholder: string;
  disabled?: boolean;
  maxHeight?: string;
  hideGroupTitles?: boolean;
}

export function GroupedMultiSelect({
  items,
  selectedIds,
  onToggleItem,
  placeholder,
  emptyMessage,
  searchPlaceholder,
  disabled = false,
  maxHeight = "60",
  hideGroupTitles = false,
}: GroupedMultiSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Count selected items for display
  const selectedCount = selectedIds.length;
  
  // Get a display string for selected items
  const selectedText = selectedCount > 0
    ? `${selectedCount} selected` 
    : placeholder;

  // Filter items based on search query with improved filtering
  const filteredItems = React.useMemo(() => {
    if (!searchQuery) return items;
    const query = searchQuery.toLowerCase().trim();
    
    return items.filter(item => {
      // Handle potentially malformed or null item names
      if (!item.name) return false;
      
      // Normalize the item name: trim whitespace and lowercase
      const normalizedName = item.name.toLowerCase().trim();
      
      // Check if the normalized name includes the query
      return normalizedName.includes(query);
    });
  }, [items, searchQuery]);

  // Group items for organized display (for the non-filtered view)
  const itemsByGroup = React.useMemo(() => {
    if (!items || items.length === 0) return [];

    // Group the filtered items
    const groupMap = filteredItems.reduce<Record<number, GroupedItem[]>>((acc, item) => {
      const group = item.groupId || 0;
      if (!acc[group]) {
        acc[group] = [];
      }
      acc[group].push(item);
      return acc;
    }, {});

    // Sort groups by groupId and items within groups by orderInGroup
    return Object.keys(groupMap)
      .map(Number)
      .sort((a, b) => a - b)
      .map(groupId => ({
        groupId,
        items: groupMap[groupId].sort((a, b) => a.orderInGroup - b.orderInGroup)
      }))
      .filter(group => group.items.length > 0);
  }, [filteredItems]);

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <div 
            role="combobox" 
            aria-expanded={isOpen} 
            className={`flex h-10 w-full cursor-pointer items-center justify-between rounded-md border border-input bg-transparent px-3 py-2 text-sm ring-offset-background focus-within:outline-none focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 ${disabled ? "cursor-not-allowed opacity-50" : ""}`}
            onClick={() => !disabled && setIsOpen(!isOpen)} // Manually handle open state for div
          >
            <span className={selectedIds.length === 0 ? 'text-muted-foreground' : 'text-card-foreground'}>
                {selectedText}
            </span>
            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </div>
      </PopoverTrigger>
      <PopoverContent className="w-[--trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder={searchPlaceholder} value={searchQuery} onValueChange={setSearchQuery} />
          <CommandEmpty>{emptyMessage}</CommandEmpty>
          <CommandGroup className={`max-h-${maxHeight} overflow-y-auto`}>
            {itemsByGroup.map((group, index) => (
              <React.Fragment key={`group-${group.groupId}`}>
                {index > 0 && <div className="h-px bg-border mx-2 my-1" />}
                {!hideGroupTitles && (
                  <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
                    Group {group.groupId}
                  </div>
                )}
                {group.items.map((item) => (
                  <CommandItem 
                    key={item.id} 
                    value={item.name} 
                    onSelect={() => onToggleItem(item.id)}
                  >
                    <Checkbox 
                      className="mr-2 h-4 w-4" 
                      checked={selectedIds.includes(item.id)} 
                    />
                    {item.name}
                  </CommandItem>
                ))}
              </React.Fragment>
            ))}
          </CommandGroup>
        </Command>
      </PopoverContent>
    </Popover>
  );
} 